import type { MedplumClient } from "@medplum/core";
import { useMedplum } from "@medplum/react-hooks";
import type { FhirResource, SubscriptionCallback } from "@vibrant/models/fhir/FhirResource";
import { useEffect, useRef } from "react";
import { createLogger } from "../logger";
import { notifyError } from "../utils";

const logger = createLogger("use-model-subscription-hook");

export function stableSerialize(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) =>
      left.localeCompare(right),
    );
    return `{${entries
      .map(([key, nestedValue]) => `${JSON.stringify(key)}:${stableSerialize(nestedValue)}`)
      .join(",")}}`;
  }

  return JSON.stringify(value);
}

interface SharedSubscriptionEntry<ModelInstance> {
  listeners: Set<SubscriptionCallback<ModelInstance>>;
  errorListeners: Set<(error: unknown) => void>;
  subscription: ReturnType<typeof FhirResource.subscribeToResource>;
  subscriptionErrorHandler: (event: { type: "error"; payload: Error }) => void;
  /** Pending close timeout — cancelled if a new listener attaches before it fires. */
  closeTimer?: ReturnType<typeof setTimeout>;
}

export interface SubscriptionModel {
  name?: string;
  subscribe(
    medplum: MedplumClient,
    query: unknown,
    callback: SubscriptionCallback<unknown>,
  ): ReturnType<typeof FhirResource.subscribeToResource>;
}

type SubscriptionModelQuery<TModel extends SubscriptionModel> = Parameters<TModel["subscribe"]>[1];

type SubscriptionModelResource<TModel extends SubscriptionModel> =
  Parameters<TModel["subscribe"]>[2] extends SubscriptionCallback<infer ModelResource>
    ? ModelResource
    : never;

const sharedSubscriptionsByClient = new WeakMap<
  MedplumClient,
  WeakMap<object, Map<string, SharedSubscriptionEntry<unknown>>>
>();

function getSharedSubscriptionsForModel(medplum: MedplumClient, model: object) {
  let sharedByModel = sharedSubscriptionsByClient.get(medplum);
  if (!sharedByModel) {
    sharedByModel = new WeakMap<object, Map<string, SharedSubscriptionEntry<unknown>>>();
    sharedSubscriptionsByClient.set(medplum, sharedByModel);
  }

  let sharedByQuery = sharedByModel.get(model);
  if (!sharedByQuery) {
    sharedByQuery = new Map<string, SharedSubscriptionEntry<unknown>>();
    sharedByModel.set(model, sharedByQuery);
  }
  return sharedByQuery;
}

function closeSharedSubscription<ModelInstance>(
  sharedSubscriptions: Map<string, SharedSubscriptionEntry<unknown>>,
  sharedSubscriptionKey: string,
  sharedEntry: SharedSubscriptionEntry<ModelInstance>,
) {
  if (sharedEntry.closeTimer) {
    clearTimeout(sharedEntry.closeTimer);
    sharedEntry.closeTimer = undefined;
  }

  sharedEntry.subscription.removeEventListener?.("error", sharedEntry.subscriptionErrorHandler);
  sharedEntry.subscription.close();

  if (sharedSubscriptions.get(sharedSubscriptionKey) === sharedEntry) {
    sharedSubscriptions.delete(sharedSubscriptionKey);
  }
}

interface UseModelSubscriptionParams<TModel extends SubscriptionModel> {
  model: TModel;
  query?: SubscriptionModelQuery<TModel>;
  callback: SubscriptionCallback<SubscriptionModelResource<TModel>>;
  onError?: (error: unknown) => void;
}

/**
 * Hook to subscribe to model changes using Medplum's subscription API.
 *
 * In order for this function to work with a model the model should implement a subscribe() method
 * as follows:
 *
 * ```typescript
 * class Model {
 *   static subscribe(
 *     medplum: MedplumClient,
 *     query: Params,
 *     callback: (fhirResource: Resource, metadata: SubscriptionEventMetadata) => void,
 *   ) => {
 *     return Model.subscribeToResource(medplum, query, (resource, metadata) => {
 *       callback(new Model(resource), metadata);
 *     });
 *   }
 * }
 * ```
 *
 * Note that it only works for models that inherit from FhirResource.
 *
 * @param params - Configuration object
 * @param params.model - The model class
 * @param params.query - The query parameters. Pass `undefined` to skip subscribing.
 * @param params.callback - Called when the subscription fires. Tracked via ref so it never causes
 *   re-subscription — safe to pass inline functions or unstable references.
 * @param params.onError - Optional error hook for callers that need to degrade gracefully when
 *   subscription setup fails.
 */
export function useModelSubscription<TModel extends SubscriptionModel>({
  model,
  query,
  callback,
  onError,
}: UseModelSubscriptionParams<TModel>) {
  const medplum = useMedplum();

  // Use a ref so the callback can change without tearing down the subscription.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Stable wrapper that delegates to the latest callback via ref.
  const stableCallbackRef = useRef<SubscriptionCallback<SubscriptionModelResource<TModel>>>(
    (resource, metadata) => callbackRef.current(resource, metadata),
  );
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const stableOnErrorRef = useRef((error: unknown) => onErrorRef.current?.(error));

  // Serialize the query so changes are detected by value, not reference.
  const serializedQuery = query === undefined ? undefined : stableSerialize(query);

  // Store query in a ref so the effect always has access to the latest value
  // without needing the query object itself as a dependency.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    if (queryRef.current === undefined) return;

    const stableCallback = stableCallbackRef.current;
    const sharedSubscriptionKey = serializedQuery!;

    try {
      const sharedSubscriptions = getSharedSubscriptionsForModel(medplum, model);
      let sharedEntry = sharedSubscriptions.get(sharedSubscriptionKey) as
        | SharedSubscriptionEntry<SubscriptionModelResource<TModel>>
        | undefined;

      if (!sharedEntry) {
        const listeners = new Set<SubscriptionCallback<SubscriptionModelResource<TModel>>>();
        const errorListeners = new Set<(error: unknown) => void>();
        const subscription = model.subscribe(medplum, queryRef.current!, (resource, metadata) => {
          listeners.forEach((listener) => {
            listener(resource as SubscriptionModelResource<TModel>, metadata);
          });
        });
        let createdSharedEntry: SharedSubscriptionEntry<SubscriptionModelResource<TModel>>;
        const subscriptionErrorHandler = (event: { type: "error"; payload: Error }) => {
          const modelName = model.name ?? "model";
          logger.warn(`Realtime subscription error for ${modelName}`, {
            error: event.payload,
            errorMessage: event.payload.message,
          });
          closeSharedSubscription(sharedSubscriptions, sharedSubscriptionKey, createdSharedEntry);
          errorListeners.forEach((listener) => {
            listener(event.payload);
          });
        };
        createdSharedEntry = { listeners, errorListeners, subscription, subscriptionErrorHandler };
        subscription.addEventListener?.("error", subscriptionErrorHandler);
        sharedEntry = createdSharedEntry;
        sharedSubscriptions.set(
          sharedSubscriptionKey,
          sharedEntry as SharedSubscriptionEntry<unknown>,
        );
      } else if (sharedEntry.closeTimer) {
        // A previous cleanup scheduled a deferred close — cancel it since we have a new listener.
        clearTimeout(sharedEntry.closeTimer);
        sharedEntry.closeTimer = undefined;
      }

      sharedEntry.listeners.add(stableCallback);
      sharedEntry.errorListeners.add(stableOnErrorRef.current);

      return () => {
        const currentSharedSubscriptions = getSharedSubscriptionsForModel(medplum, model);
        const currentSharedEntry = currentSharedSubscriptions.get(sharedSubscriptionKey) as
          | SharedSubscriptionEntry<SubscriptionModelResource<TModel>>
          | undefined;

        if (currentSharedEntry) {
          currentSharedEntry.listeners.delete(stableCallback);
          currentSharedEntry.errorListeners.delete(stableOnErrorRef.current);

          if (currentSharedEntry.listeners.size === 0) {
            // Defer the close so React Strict Mode's synchronous remount (or rapid
            // re-renders) can reattach a listener before we tear down the WebSocket.
            currentSharedEntry.closeTimer = setTimeout(() => {
              // Re-check — a new listener may have attached during the grace period.
              if (currentSharedEntry.listeners.size === 0) {
                closeSharedSubscription(
                  currentSharedSubscriptions,
                  sharedSubscriptionKey,
                  currentSharedEntry,
                );
              }
            }, 0);
          }
        }
      };
    } catch (error) {
      const modelName = model.name ?? "model";
      notifyError(`Failed to create subscription for ${modelName}`, {
        notifyUser: false,
        error: error instanceof Error ? error : undefined,
        logger,
      });
      onErrorRef.current?.(error);
    }
  }, [serializedQuery, medplum, model]);
}
